import React, { useRef, useState } from 'react';
import { Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { 
  uploadFile, 
  postDocumentoAdmin, 
  downloadFile3,
  deleteDocument,
  deleteDocuments 
} from '../../../../services/userservice';

export const DocumentSection = ({ 
  documents, 
  onDocumentUploaded, 
  onDocumentDeleted,
  userId,
  entityId,
  entityType 
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null); // ID del documento que se está eliminando
  const [docToDelete, setDocToDelete] = useState(null); // Documento seleccionado para confirmar eliminación

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // Subir archivo y obtener nombre
      const filename = await uploadFile(file);
      
      // Registrar documento en BD
      const tipoDocumento = file.type;
      await postDocumentoAdmin(filename, tipoDocumento, userId);
      
      // Notificar al padre para recargar datos
      onDocumentUploaded?.(filename);
    } catch (error) {
      console.error('Error al subir documento:', error);
      alert('Error al subir el documento');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = (docName) => {
    downloadFile3(docName);
  };

  const confirmDelete = (doc) => {
    setDocToDelete(doc);
  };

  const cancelDelete = () => {
    setDocToDelete(null);
  };

  const executeDelete = async () => {
    if (!docToDelete) return;

    setDeleting(docToDelete.id);

    try {
        const results = await Promise.allSettled([
        deleteDocuments(docToDelete.id),
        deleteDocument(docToDelete.name),
        ]);

        const [res1, res2] = results;

        const errors = [];

        if (res1.status === 'rejected') {
        errors.push(`deleteDocuments falló: ${res1.reason}`);
        }

        if (res2.status === 'rejected') {
        errors.push(`deleteDocument falló: ${res2.reason}`);
        }

        if (errors.length > 0) {
        throw new Error(errors.join(' | '));
        }

        // Si ambas funcionaron
        onDocumentDeleted?.(docToDelete.id || docToDelete.name);
        setDocToDelete(null);

    } catch (error) {
        console.error('Error al eliminar documento:', error);
        alert(error.message || 'Error al eliminar el documento.');
    } finally {
        setDeleting(null);
    }
    };

  return (
    <>
      <hr />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="text-muted mb-0">Documentos adjuntos</h6>
        <Button 
          color="dark" 
          size="sm" 
          onClick={handleUploadClick} 
          disabled={uploading}
        >
          {uploading ? <Spinner size="sm" /> : 'Subir documento'}
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        />
      </div>
      
      {documents.length === 0 ? (
        <p className="text-muted small">Sin documentos adjuntos.</p>
      ) : (
        <ul className="list-unstyled mb-0">
          {documents.map((doc, idx) => (
            <li 
              key={doc.id ?? `doc-${idx}`} 
              className="mb-2 d-flex align-items-center justify-content-between"
            >
              <Button
                color="link"
                onClick={() => handleDownload(doc.name)}
                className="p-0 text-start text-decoration-none"
                disabled={deleting === doc.id}
              >
                {doc.name}
              </Button>
              
              <Button
                color="danger"
                size="sm"
                outline
                onClick={() => confirmDelete(doc)}
                disabled={deleting === doc.id}
                className="ms-2"
                title="Eliminar documento"
              >
                {deleting === doc.id ? (
                  <Spinner size="sm" />
                ) : (
                  'ELIMINAR'
                )}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Modal de confirmación para eliminar */}
      <Modal isOpen={!!docToDelete} toggle={cancelDelete} size="sm" centered zIndex={10000}>
        <ModalHeader toggle={cancelDelete} className="text-danger">
          Confirmar eliminación
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            ¿Está seguro de que desea eliminar el archivo 
            <strong className="d-block mt-2 text-break">
              "{docToDelete?.name}"
            </strong>
          </p>
          <p className="text-muted small mt-2 mb-0">
            Esta acción no se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <Button color="secondary" size="sm" onClick={cancelDelete}>
            Cancelar
          </Button>
          <Button 
            color="danger" 
            size="sm" 
            onClick={executeDelete}
          >
            {deleting ? (
              <>
                <Spinner size="sm" className="me-1" />
                Eliminando...
              </>
            ) : (
              'Sí, eliminar'
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};